import { useState, useCallback, useRef } from 'react';
import classNames from 'classnames';
import styles from './FormfieldSelect.module.scss';
import { ErrorMessage, Label, ConditionalWrapper, LabelProps } from 'ui/components';

export type Option = {
	value: string;
	text: string;
	disabled?: boolean;
	hidden?: boolean;
	selected?: boolean;
};

type State = {
	required?: boolean;
	disabled?: boolean;
	hasError?: boolean;
	isHidden?: boolean;
};

export interface FormfieldSelectProps {
	className?: string;
	id: string;
	multiple?: boolean;
	name?: string;
	label?: string;
	labelStyle?: LabelProps['labelStyle'];
	options: Option[];
	size?: number;
	register?: any;
	errorMessage?: string;
	state?: State;
	defaultValue?: string;
	handleChange?: any;
	layout?: 'row' | 'row-slim' | 'column';
	variant?: 'default' | 'website';
}

const ariaLabel = (state: State, label: string) => {
	if (state?.['isHidden']) return label;
};

const ariaError = (state: State, name: string, id: string) => {
	if (state?.['hasError']) return name + id;
};

export const FormfieldSelect: React.FC<FormfieldSelectProps> = ({
	className,
	register,
	errorMessage,
	state,
	id,
	label,
	labelStyle,
	options,
	name,
	multiple,
	size,
	defaultValue,
	handleChange,
	layout,
	variant,
}) => {
	const { required, disabled, hasError } = state ?? {};
	const [isOpen, setIsOpen] = useState(false);
	const lastPointerDown = useRef(0);

	const open = useCallback(() => setIsOpen(true), []);
	const close = useCallback(() => setIsOpen(false), []);

	// mouse/touch/pen
	const handlePointerDown = useCallback(() => {
		lastPointerDown.current = performance.now();
		setIsOpen((isOpen) => !isOpen); // Chrome/Edge/Safari path
	}, []);

	const handleSelectChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			handleChange?.(e);
			close();
		},
		[handleChange],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLSelectElement>) => {
			if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === ' ' || (e.altKey && e.key === 'ArrowDown')) open();
			if (e.key === 'Escape' || (e.altKey && e.key === 'ArrowUp')) close();
		},
		[open, close],
	);

	return (
		<div
			className={classNames(
				styles.FormfieldSelect,
				className,
				{ [styles[`FormfieldSelect___${layout}`]]: layout },
				{ [styles[`FormfieldSelect___${variant}`]]: variant },
				{ [styles.hasError]: hasError },
				{ [styles.isDisabled]: disabled },
			)}
		>
			{label && (
				<Label
					id={id}
					className={classNames(styles.FormfieldSelect_label, styles[`FormfieldSelect_label___${labelStyle}`])}
					state={state}
					labelStyle={labelStyle}
				>
					{label}
				</Label>
			)}

			<ConditionalWrapper enabled={layout === 'row' ? true : false} className={classNames(styles.FormfieldString__input_wrapper)}>
				<div
					className={classNames(
						styles.FormfieldSelect_wrapper,
						{
							[styles.isMultiple]: multiple === true,
						},
						{ [styles.isOpen]: isOpen },
					)}
				>
					<select
						name={name}
						id={id}
						className={classNames(styles.FormfieldSelect_element, {
							[styles.hasError]: hasError,
						})}
						aria-label={ariaLabel(state, label)}
						aria-describedby={ariaError(state, name, id)}
						multiple={multiple}
						size={size}
						required={required}
						disabled={disabled}
						defaultValue={defaultValue}
						{...(register && { ...register(name, { required }) })}
						onChange={handleSelectChange}
						onPointerDown={handlePointerDown}
						onFocus={open}
						onKeyDown={handleKeyDown}
					>
						{options.map((option, index) => (
							<option value={option.value} disabled={option.disabled} hidden={option.hidden} key={index}>
								{option.text}
							</option>
						))}
					</select>
				</div>
				{hasError && <ErrorMessage>{errorMessage}</ErrorMessage>}
			</ConditionalWrapper>
		</div>
	);
};
